import React, { useCallback, useRef } from 'react';
import useLinkedScrollLeft from '../../hooks/useLinkedScrollLeft';
import useDragScroll from '../../hooks/useDragScroll';
import useHorizontalOverflow from '../../hooks/useHorizontalOverflow';
import StickyScrollbar from '../ui/StickyScrollbar';
import { PAGE_SCROLL_PANE_X } from '../../constants/stickyTable';

/**
 * A table's horizontal scroller for the Budgeting pages: an x-only pane plus the
 * shared `ui/StickyScrollbar`, shown only while the table overflows
 * (2026-10-05).
 *
 * WHY: owner, of `/budget/overview`: *"doesn't fit on the page unless I close
 * the side menu. No ability to scroll."* The table's only horizontal scrollbar
 * sat under its last row — below the window on any real report — against the
 * 2026-08-10 ruling quoted in `ui/StickyScrollbar.js` (*"horizontal scroll bars
 * in table views on all pages should be sticky"*). Each budget version adds a
 * ~115px column, so the overview overflows at 1280px with the sidebar open once
 * there are two or more previous versions. The Manage Budgets tables (Cost
 * Codes, Payment Types, Crew Grades, the since-retired Ad-hoc lines) had the same plain
 * `overflow-x-auto` wrapper.
 *
 * - The pane hides its OWN bar (`PAGE_SCROLL_PANE_X`) so a table whose bottom is
 *   on screen never shows two bars; it stays the real keyboard/touch/trackpad
 *   scroller. The sticky bar is the mouse user's duplicate, linked two-way by
 *   `hooks/useLinkedScrollLeft`.
 * - Overflow is MEASURED (`scrollWidth > clientWidth`, re-measured by a
 *   `ResizeObserver` on the pane and on the table) — the version count, the
 *   sidebar and the user's column widths all change it. jsdom measures 0, so
 *   in tests the bar is absent unless the measurements are stubbed.
 * - The bar's spacer is the pane's `scrollWidth`, so the two share one scroll
 *   range (both are full-width block children of the same parent).
 *
 * 🚨 NO ANCESTOR MAY BE `overflow-hidden`. A `sticky` bar pins to its nearest
 * scroll container, and `overflow: hidden` makes one — the bar then pins to
 * that card (i.e. stays at the table's bottom, off-screen). Use `overflow-clip`
 * to clip rounded corners (`CrewMembersTableFrame`'s precedent); that is why the
 * overview card is `overflow-clip`.
 *
 * Not `ui/PageScrollTable`: these tables have expandable parent/child rows (the
 * overview), a resizable colgroup + drag-reorder (Cost Codes), or nothing to
 * pin; adopting the shell is a separate change. This component touches no
 * table markup — it only wraps it.
 *
 * 🔼 Nothing in it is budget-specific: it is a CANDIDATE FOR `components/ui/`
 * (with a root Shared Modules row) once root `CLAUDE.md` is free to edit.
 *
 * @param {string} testId          prefix: `${testId}` pane, `${testId}-scrollbar` bar,
 *                                 `${testId}-frame` wrapper
 * @param {Function|object} scrollerRef optional extra ref for the pane (a caller
 *                                 that measures the scrollport, e.g. Cost Codes)
 * @param {string} wrapperClassName classes on the wrapper (e.g. `hidden md:block`)
 * @param {boolean} dragPan        OPT-IN click-and-drag panning (2026-10-05, owner, of
 *                                 `/budget/overview`: "use the click and drag table method"):
 *                                 `hooks/useDragScroll`'s page-scroll TABLE mode — a vertical
 *                                 drag scrolls the window, a horizontal one pans THIS pane.
 *                                 Default OFF: the Manage Budgets tables have editable cells and
 *                                 resize handles. 🚨 The hook resolves the horizontal target from
 *                                 `[data-page-scroll-pane]`, which `PAGE_SCROLL_PANE_X` does NOT
 *                                 carry (classes only), so the pane gets the attribute only while
 *                                 this is on. The hook skips buttons, links and inputs and
 *                                 `[data-pan-skip]`, and swallows the click a real pan produces.
 * @param {?number} stickyBottom   MEASURED px clearance above a fixed bottom bar
 *                                 (Manage Budgets' totals/save bar — the same
 *                                 figure the crew table uses); `null` keeps
 *                                 `ui/StickyScrollbar`'s nav-clearing classes.
 */
export default function BudgetTableScroller({
  testId,
  scrollerRef = null,
  wrapperClassName = '',
  stickyBottom = null,
  dragPan = false,
  children,
}) {
  const paneRef = useRef(null);
  const frameRef = useRef(null);
  // The ref is attached to the frame only while `dragPan` is on; without it the
  // hook finds no element and attaches no listener.
  useDragScroll(frameRef, { axis: 'both', verticalPansWindow: true });
  const barRef = useRef(null);
  // The measurement lives in `hooks/useHorizontalOverflow` (moved out of here
  // 2026-10-05 so the Crew Gantt's page-scroll timeline can share it).
  const { overflows, scrollWidth } = useHorizontalOverflow(paneRef);

  const setPane = useCallback((el) => {
    paneRef.current = el;
    if (typeof scrollerRef === 'function') scrollerRef(el);
    else if (scrollerRef) scrollerRef.current = el;
  }, [scrollerRef]);

  useLinkedScrollLeft([paneRef, barRef]);

  return (
    <div ref={dragPan ? frameRef : undefined} className={wrapperClassName} data-testid={`${testId}-frame`}>
      <div
        ref={setPane}
        className={PAGE_SCROLL_PANE_X}
        data-page-scroll-pane={dragPan ? '' : undefined}
        data-testid={testId}
      >
        {children}
      </div>
      {overflows ? (
        <StickyScrollbar
          ref={barRef}
          contentStyle={{ width: scrollWidth }}
          testId={`${testId}-scrollbar`}
          style={stickyBottom == null ? undefined : { bottom: stickyBottom }}
        />
      ) : null}
    </div>
  );
}
